import { Fee, FeePrice, ProrationProps } from "../../types/fee-types";
import { getOneTimeFees, isFeePerApplicant, isFeeMonthly } from "./getFees";

import {
  formatPriceDisplay,
  parseDateToUTC,
} from "../../helpers/string-helpers";
import { FeeTransparencyState } from "../../types/fee-state-types";
import { AvailableUnitExtended, Price } from "../../types/property-types";

// convert percentage amount to an actual percentage
/*--------------------------------------------------------------------------------------------------*/
function getPercent(percent: number) {
  return percent / 100;
}

// Calculates the ratio of the day of the month to the total days in that month.
export function proRateAmount(moveInDate: string): number {
  // Parse the date string in local time if provided, otherwise use today
  const date = moveInDate
    ? (() => {
        const [year, month, day] = moveInDate.split("-").map(Number);
        return new Date(year, month - 1, day);
      })()
    : new Date();
  const dayOfMonth = date.getDate();
  const daysInMonth = new Date(
    date.getFullYear(),
    date.getMonth() + 1,
    0,
  ).getDate();

  return (dayOfMonth - 1) / daysInMonth;
}

// Cap a fee at a max amount
/*--------------------------------------------------------------------------------------------------*/
function capFee(fee: Fee, amount: number, moveInDate: string, isFmlp: boolean) {
  const proration = 1 - proRateAmount(moveInDate);
  const maxAmount = fee.valueCap ? parseFloat(fee.valueCap) : null;

  if (fee.valueCap) {
    if (fee.prorationMethod === "calendar_day_month" && isFmlp) {
      const proratedCap = parseFloat(fee.valueCap) * proration;
      if (proratedCap < amount) {
        return proratedCap;
      }
      return amount;
    }

    if (maxAmount && amount > maxAmount) {
      return maxAmount;
    }
  }

  return amount;
}

function getFeeMultiplier(
  fee: Fee,
  numberOfApplicants: number,
  moveInDate: string,
  isFmlp: boolean = false,
): number {
  if (
    isFeePerApplicant(fee) &&
    fee.frequency === "one_time" &&
    fee.isRequired === true
  ) {
    return fee.activeCount * numberOfApplicants;
  }

  if (isFmlp && fee.prorationMethod === "calendar_day_month") {
    return fee.activeCount * (1 - proRateAmount(moveInDate));
  }

  return fee.activeCount;
}

export const getMonthlyAdditionalPetFees = (fees: Fee[]) => {
  return fees.filter((fee) => fee.isAdditionalPetFee && isFeeMonthly(fee));
};

export function calculateSingleFee(
  fee: Fee,
  selectedBaseRent: number,
  numberOfApplicants: number,
  moveInDate: string,
  isFmlp: boolean = false,
): FeePrice {
  const multiplier = getFeeMultiplier(
    fee,
    numberOfApplicants,
    moveInDate,
    isFmlp,
  );

  let feeAmount = 0;
  if (fee.amount != null) {
    feeAmount = fee.amount;
  } else if (fee.percentageAmount != null) {
    feeAmount = getPercent(fee.percentageAmount) * selectedBaseRent;
  }
  const amountBeforeCap = feeAmount * multiplier;
  const finalAmount = capFee(fee, amountBeforeCap, moveInDate, isFmlp);
  let minAmount = null;
  if (fee.minAmount != null) {
    const minAmountBeforeCap = fee.minAmount * multiplier;
    minAmount = capFee(fee, minAmountBeforeCap, moveInDate, isFmlp);
  }

  let maxAmount = null;
  if (fee.maxAmount != null) {
    const maxAmountBeforeCap = fee.maxAmount * multiplier;
    maxAmount = capFee(fee, maxAmountBeforeCap, moveInDate, isFmlp);
  }

  return {
    amount: minAmount ?? finalAmount,
    maxAmount:
      maxAmount && minAmount && maxAmount > minAmount ? maxAmount : null,
  };
}

// Get the sum of the fee on the summary step
export const getFeeSum = (
  feeState: FeeTransparencyState,
  fee?: Fee,
  proration?: ProrationProps,
) => {
  if (proration) {
    return formatPriceDisplay(
      proration.feePrice.amount,
      proration.feePrice.maxAmount,
      proration.variableCosts,
    );
  }
  if (fee) {
    const feeAmount = calculateSingleFee(
      fee,
      feeState?.selectedBaseRent,
      feeState?.numberOfApplicants,
      feeState.selectedMoveInDate,
    );
    return (
      fee.textAmount ??
      formatPriceDisplay(feeAmount.amount, feeAmount.maxAmount)
    );
  }
  return "";
};

// Get the price for the fee line item
export const getFeeLinePrice = (
  feeState: FeeTransparencyState,
  fee?: Fee,
  proration?: ProrationProps,
) => {
  if (fee) {
    if (fee.percentageAmount) {
      const amount =
        getPercent(fee.percentageAmount) * feeState.selectedBaseRent;
      return fee.textAmount ?? formatPriceDisplay(amount);
    }
    const amount = fee?.minAmount ?? fee?.amount ?? 0;
    return fee.textAmount ?? formatPriceDisplay(amount, fee?.maxAmount);
  }
  if (proration) {
    return formatPriceDisplay(
      proration.feePrice.amount,
      proration.feePrice.maxAmount,
      proration.variableCosts,
    );
  }
  return "";
};

export function calculateFees(
  selectedBaseRent: number,
  fees: Fee[],
  numberOfApplicants: number,
  moveInDate: string,
  isFmlp = false,
): FeePrice {
  let totalAmountSum = 0;
  let totalMaxAmountSum = 0;

  if (fees) {
    for (const fee of fees) {
      const singleFeeCalc = calculateSingleFee(
        fee,
        selectedBaseRent,
        numberOfApplicants,
        moveInDate,
        isFmlp,
      );
      totalAmountSum += singleFeeCalc.amount;
      if (singleFeeCalc.maxAmount) {
        totalMaxAmountSum += singleFeeCalc.maxAmount;
      } else {
        totalMaxAmountSum += singleFeeCalc.amount;
      }
    }
  }

  return {
    amount: totalAmountSum,
    maxAmount: totalMaxAmountSum > totalAmountSum ? totalMaxAmountSum : null,
  };
}

// Calculate TMLP Fees
/*--------------------------------------------------------------------------------------------------*/
export function calculateTMLP(
  selectedBaseRent: number,
  fees: Fee[],
  numberOfApplicants = 1,
  moveInDate: string,
) {
  const monthlyFees = fees?.filter((fee) => isFeeMonthly(fee));
  const tmlp = calculateFees(
    selectedBaseRent,
    monthlyFees,
    numberOfApplicants,
    moveInDate,
  );

  // tmlp == active optional and required fees plus base rent
  tmlp.amount += selectedBaseRent;
  if (tmlp.maxAmount) {
    tmlp.maxAmount += selectedBaseRent;
  }
  return tmlp;
}

// Calculates the proration of the fees based on the move in date
export function calculateProration(
  selectedBaseRent: number,
  fees: Fee[],
  numberOfApplicants = 1,
  moveInDate: string,
): ProrationProps {
  if (!moveInDate) {
    return null;
  }

  const prorationAmount = proRateAmount(moveInDate);
  const proratedFees = fees?.filter((fee) => isFeeMonthly(fee));
  const hasVariableCosts = proratedFees?.some((fee) => fee.textAmount);

  //If it's the first of the month, early return tmlp
  if (!prorationAmount || prorationAmount === 0) {
    const tmlp = calculateTMLP(
      selectedBaseRent,
      fees,
      numberOfApplicants,
      moveInDate,
    );
    return { feePrice: tmlp, variableCosts: hasVariableCosts };
  }

  const leasePrice = calculateFees(
    selectedBaseRent,
    proratedFees,
    numberOfApplicants,
    moveInDate,
    true,
  );

  const baseRentToAdd = selectedBaseRent - selectedBaseRent * prorationAmount;

  leasePrice.amount += baseRentToAdd;
  if (leasePrice.maxAmount) {
    leasePrice.maxAmount += baseRentToAdd;
  }

  return { feePrice: leasePrice, variableCosts: hasVariableCosts };
}

// Calculate One Time Fees
/*--------------------------------------------------------------------------------------------------*/
export function calculateOneTimeFees(feeState: FeeTransparencyState) {
  const proration = calculateProration(
    feeState.selectedBaseRent,
    feeState.allFees,
    feeState.numberOfApplicants,
    feeState.selectedMoveInDate,
  );
  const oneTimeFees = calculateFees(
    feeState.selectedBaseRent,
    getOneTimeFees(feeState.allFees ?? []),
    feeState.numberOfApplicants,
    feeState.selectedMoveInDate,
  );

  if (proration) {
    return sumFeePrices([oneTimeFees, proration.feePrice]);
  }
  return oneTimeFees;
}

// Calculate FeeMap
/*--------------------------------------------------------------------------------------------------*/
export function calculateFeeMap(
  selectedBaseRent: number,
  feeMap: Map<string, Fee[]>,
  numberOfApplicants: number,
  moveInDate: string,
) {
  let fees: Fee[] = [];
  feeMap.forEach((entry) => {
    fees = fees.concat(entry);
  });
  return calculateFees(selectedBaseRent, fees, numberOfApplicants, moveInDate);
}

// Render Fee Amount Text as a string
// This is used for the fee line items text based price information
/*--------------------------------------------------------------------------------------------------*/
export function renderFeePriceText(
  fee: Fee,
  selectedBaseRent: number,
  moveInDate: string,
  numberOfApplicants: number,
) {
  const calculatedFee = calculateSingleFee(
    fee,
    selectedBaseRent,
    numberOfApplicants,
    moveInDate,
    false,
  );
  if (fee.textAmount) {
    return fee.textAmount;
  }
  if (
    calculatedFee.amount &&
    calculatedFee.maxAmount &&
    calculatedFee.amount !== calculatedFee.maxAmount
  ) {
    const rangePrice = formatPriceDisplay(
      calculatedFee.amount,
      calculatedFee.maxAmount,
    );
    return rangePrice;
  }
  const amountPrice = formatPriceDisplay(
    calculatedFee.amount,
    calculatedFee.maxAmount,
  );
  return amountPrice;
}

// checks if there is any usage based fees included in selected personalized add ons
/*--------------------------------------------------------------------------------------------------*/
function hasTextAmountInFeeMap(feeMap: Map<string, Fee[]>): boolean {
  const feeArrays = feeMap.values();
  for (const feeArray of feeArrays) {
    if (feeArray.some((fee) => fee.textAmount && fee.activeCount > 0)) {
      return true;
    }
  }
  return false;
}

// Sum up multiple FeePrice objects
/*--------------------------------------------------------------------------------------------------*/
function sumFeePrices(feePrices: FeePrice[]): FeePrice {
  return feePrices.reduce(
    (sum, feePrice) => {
      const newSum: FeePrice = {
        amount: (sum.amount ?? 0) + (feePrice.amount ?? 0),
        maxAmount: null,
      };

      // If any of the fee prices have a maxAmount, calculate the sum of maxAmounts
      if (feePrice.maxAmount != null || sum.maxAmount != null) {
        newSum.maxAmount =
          (sum.maxAmount ?? sum.amount ?? 0) +
          (feePrice.maxAmount ?? feePrice.amount ?? 0);
      }

      return newSum;
    },
    { amount: 0, maxAmount: null },
  );
}

// Gets the subtotal of the category and concatenates the amount and text for usage based costs.
/*--------------------------------------------------------------------------------------------------*/
export function getSubTotal(
  selectedBaseRent: number,
  numberOfApplicants: number,
  moveInDate: string,
  fees?: Fee[],
  feeMap?: Map<string, Fee[]>,
  defaultSubtotal: string | null = null,
  proration?: FeePrice | null,
): string | null {
  if (fees) {
    const calculatedFees = calculateFees(
      selectedBaseRent,
      fees,
      numberOfApplicants,
      moveInDate,
    );

    const FeesPlusProration = proration
      ? sumFeePrices([calculatedFees, proration])
      : calculatedFees;
    const hasUsageText = fees.some((fee) => fee.textAmount);
    return formatPriceDisplay(
      FeesPlusProration.amount,
      FeesPlusProration.maxAmount,
      hasUsageText,
    );
  } else if (feeMap) {
    const filteredMap = new Map(
      Array.from(feeMap.entries()).map(([group, feeItems]) => [
        group,
        feeItems.filter((fee) => isFeeMonthly(fee)),
      ]),
    );
    const calculatedFees = calculateFeeMap(
      selectedBaseRent,
      filteredMap,
      numberOfApplicants,
      moveInDate,
    );
    const hasUsageText = hasTextAmountInFeeMap(filteredMap);
    return formatPriceDisplay(
      calculatedFees.amount,
      calculatedFees.maxAmount,
      hasUsageText,
    );
  }
  return defaultSubtotal;
}

// //Checks if any personalized add ons have been selected
export function hasActiveCount(feeMap: Map<string, Fee[]>): boolean {
  for (const [, items] of feeMap) {
    for (const item of items) {
      if (item.activeCount > 0 && item.frequency === "monthly") {
        return true;
      }
    }
  }
  return false;
}

export function getMaxDate(selectedUnit?: AvailableUnitExtended) {
  if (!selectedUnit) {
    return null;
  }
  const isFlatPricing = selectedUnit.isFlatPricing;
  const availableOn = selectedUnit.availableOn;
  if (isFlatPricing) {
    const utcAvailableOn = parseDateToUTC(availableOn);
    const now = new Date();
    const utcNow = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
    );
    let baseDate;
    if (utcAvailableOn < utcNow) {
      baseDate = utcNow;
    } else {
      baseDate = utcAvailableOn;
    }
    baseDate.setUTCDate(baseDate.getUTCDate() + 30);
    const result = baseDate.toISOString().slice(0, 10);
    return result;
  }
  const maxDate =
    selectedUnit?.prices?.[selectedUnit?.prices?.length - 1]?.leaseStartsOn;
  return maxDate;
}

export function getMoveinDate(
  moveInDate: string,
  selectedUnit: AvailableUnitExtended,
) {
  const maxDate = getMaxDate(selectedUnit);
  const moveInDateObj = parseDateToUTC(moveInDate);
  const todayString = new Date().toISOString().slice(0, 10);
  const todayUTC = parseDateToUTC(todayString);
  const availableOnDateObj = parseDateToUTC(selectedUnit.availableOn);
  const effectiveAvailableOnDate =
    availableOnDateObj < todayUTC ? todayUTC : availableOnDateObj;
  const maxDateObj = maxDate ? parseDateToUTC(maxDate) : null;

  const isMoveInDateValid =
    moveInDateObj >= effectiveAvailableOnDate &&
    maxDateObj &&
    moveInDateObj <= maxDateObj;

  if (isMoveInDateValid) {
    return moveInDate;
  }

  return effectiveAvailableOnDate.toISOString().slice(0, 10);
}

export function getLeaseTerm(
  leaseTerm: string | number,
  prices: Price[],
  isFlatPricing: boolean,
) {
  if (isFlatPricing) {
    return "12";
  }

  const isValidTerm = prices.some((price) => price.leaseTerm === leaseTerm);

  if (isValidTerm) {
    return leaseTerm;
  }

  const sortedPrices = prices.sort(
    (a, b) => parseFloat(a.price) - parseFloat(b.price),
  );
  return sortedPrices[0].leaseTerm;
}
